import React, { useState, useEffect, useCallback } from 'react';
import { Play, MapPin, X, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { propertyData } from '../../data/mock';

// Global playback manager - ensures only one video plays at a time
let globalActivePlayer = null;
const setGlobalActivePlayer = (playerRef) => {
  if (globalActivePlayer && globalActivePlayer !== playerRef) {
    if (globalActivePlayer.pause) {
      globalActivePlayer.pause();
    }
  }
  globalActivePlayer = playerRef;
};

export { setGlobalActivePlayer };

const HeroSection = ({ onInquire, onVideoPlay }) => {
  const [isMobile, setIsMobile] = useState(false);
  const [showVideo, setShowVideo] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // YouTube video ID from propertyData
  const youtubeId = propertyData.heroYouTubeId;

  // Detect mobile device
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Handle iframe load complete
  const handleIframeLoad = useCallback(() => {
    setIsLoading(false);
  }, []);

  // Play video - shows YouTube embed
  const playVideo = useCallback(() => {
    setIsLoading(true);
    setShowVideo(true);
    
    // Register as active player
    setGlobalActivePlayer({
      pause: () => {
        setShowVideo(false);
      }
    });
    
    if (onVideoPlay) onVideoPlay(true);
  }, [onVideoPlay]);

  // Close video
  const closeVideo = useCallback(() => {
    setShowVideo(false);
    setIsLoading(false);
    if (onVideoPlay) onVideoPlay(false);
  }, [onVideoPlay]);

  return (
    <section id="hero" className="relative h-screen w-full overflow-hidden" data-testid="hero-section">
      {/* Hero Container */}
      <div className="absolute inset-0">
        {/* Static Hero Image - Always visible when video not playing */}
        <div className={`absolute inset-0 transition-opacity duration-700 ${showVideo ? 'opacity-0' : 'opacity-100'}`}>
          <img
            src={propertyData.images.hero}
            alt="William Lane Estate"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/60" />
        </div>
        
        {/* YouTube Embed - Only shown when playing (single iframe for both mobile/desktop) */}
        {showVideo && (
          <div className={`${isMobile ? 'fixed inset-0 z-[100]' : 'absolute inset-0'} bg-black`}>
            <iframe
              className="w-full h-full"
              src={`https://www.youtube-nocookie.com/embed/${youtubeId}?autoplay=1&rel=0&modestbranding=1&playsinline=1`}
              title="William Lane Estate Film"
              frameBorder="0"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              onLoad={handleIframeLoad}
              data-testid="hero-youtube-iframe"
            />
            
            {/* Close button */}
            <button
              onClick={closeVideo}
              className="absolute top-4 right-4 z-20 w-12 h-12 rounded-full bg-black/60 backdrop-blur-md flex items-center justify-center text-white hover:bg-black/80 transition-all"
              data-testid="close-hero-video-btn"
            >
              <X className="w-6 h-6" />
            </button>
            
            {/* Loading overlay */}
            {isLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/60 z-10 pointer-events-none">
                <Loader2 className="w-12 h-12 text-amber-400 animate-spin" />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Hero Content Overlay */}
      <div className={`relative z-10 h-full flex flex-col justify-end pb-40 px-4 sm:px-6 lg:px-8 transition-opacity duration-500 ${showVideo ? 'opacity-0 pointer-events-none' : 'opacity-100'}`}>
        <div className="max-w-5xl">
          {/* Location Badge */}
          <div className="inline-flex items-center px-4 py-2 bg-white/10 backdrop-blur-md rounded-full border border-white/20 mb-6">
            <MapPin className="w-4 h-4 text-amber-400 mr-2" />
            <span className="text-white/90 text-sm font-medium">Eustis, Florida</span>
          </div>
          
          {/* Main Headline */}
          <h1 className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-serif text-white leading-tight mb-6">
            <span className="block font-bold tracking-wide">WILLIAM LANE</span>
            <span className="block font-bold tracking-wide text-amber-400">LEGACY ESTATE</span>
          </h1>
          
          {/* Subheadline */}
          <p className="text-lg sm:text-xl text-white/90 font-bold max-w-2xl mb-8">
            A 20-acre Central Florida legacy property featuring exceptional infrastructure, 
            multiple structures, and unlimited potential.
          </p>
          
          {/* Price & Features */}
          <div className="flex flex-wrap items-baseline gap-x-3 gap-y-2 mb-8">
            <span className="text-3xl sm:text-4xl font-serif font-bold text-amber-400">$2,300,000</span>
            <span className="text-white/60 font-bold">|</span>
            <span className="text-white/90 font-bold">20 Acres</span>
            <span className="text-amber-400 font-bold">+</span>
            <span className="text-white/90 font-bold">6-Structures</span>
            <span className="text-amber-400 font-bold">+</span>
            <span className="text-white/90 font-bold">NO HOA</span>
            <span className="text-amber-400 font-bold">+</span>
            <span className="text-white/90 font-bold text-sm sm:text-base">Auto Lift - Workshops - Hobby Farm - Sauna - Unmatched Privacy</span>
          </div>
          
          {/* CTA Buttons */}
          <div className="flex flex-wrap gap-4">
            <Button 
              onClick={onInquire}
              className="bg-amber-500 hover:bg-amber-600 text-black font-semibold px-8 py-6 text-lg rounded-full"
              data-testid="hero-inquire-btn"
            >
              Private Inquiry
            </Button>
            <Button 
              variant="outline"
              onClick={() => document.getElementById('gallery')?.scrollIntoView({ behavior: 'smooth' })}
              className="border-white/30 text-white hover:bg-white/10 px-8 py-6 text-lg rounded-full"
              data-testid="hero-gallery-btn"
            >
              View Gallery
            </Button>
          </div>
        </div>
      </div>

      {/* Play Button - Visible when not playing */}
      {!showVideo && (
        <div className="absolute bottom-32 left-1/2 -translate-x-1/2 z-20 animate-in fade-in duration-500">
          <button
            onClick={playVideo}
            className="group flex flex-col items-center"
            data-testid="play-estate-film-btn"
          >
            <div className="w-20 h-20 rounded-full bg-white/20 backdrop-blur-md border-2 border-amber-400/60 flex items-center justify-center text-white hover:bg-amber-500/30 hover:border-amber-400 transition-all duration-300 hover:scale-110 shadow-2xl group-hover:shadow-amber-500/30">
              <Play className="w-8 h-8 ml-1" />
            </div>
            <span className="mt-3 text-white text-sm font-medium tracking-wide bg-black/50 backdrop-blur-sm rounded-full px-4 py-1.5">
              PLAY ESTATE FILM — SOUND ON
            </span>
          </button>
        </div>
      )}
    </section>
  );
};

export default HeroSection;
